import { useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { observer } from "@legendapp/state/react";
import type { ChapterFeedV2 } from "../types";
import { lesson$, isPaused$, activityVisible$, generatedActivities$, nextLesson } from "../state/videoState";
import { VideoLayout } from "./VideoLayout";
import { VIDEO_MAX_W } from "./videoConstants";
import { FeedActivityCarousel } from "./FeedActivityCarousel";
import { stepForward, stepBackward, nextChapter, feedScrollRequest$ } from "../state/feedState";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

interface HorizontalLessonCarouselProps {
  chapter: ChapterFeedV2;
  onToggleLike: () => void;
}

/**
 * Each lesson has two horizontal panels: [video, activity].
 * Panel ordering in the scroll container:
 *   [prev-lesson-video, prev-lesson-activity?, current-lesson-video, current-lesson-activity, next-lesson-video, ...]
 * We keep it simple: always render prev-lesson (video only), current-lesson (video + activity),
 * next-lesson (video only). Within the current lesson, the sub-panel tracks via activityVisible$.
 */
const HorizontalLessonCarousel = observer(function HorizontalLessonCarousel({
  chapter,
  onToggleLike,
}: HorizontalLessonCarouselProps) {
  const lessonIndex = lesson$.currentLessonIndex.get();
  const totalLessons = chapter.lessonIDs.length;

  const scrollRef = useRef<HTMLDivElement>(null);
  const isResettingScroll = useRef(false);
  const scrollPausedByUs = useRef(false);

  // Horizontal lesson telemetry (iOS handleViewableItemsChanged, FeedNew.tsx:738-763):
  // fire feed_lesson_swiped on every lesson-index change (direction-aware) and
  // feed_chapter_last_lesson_reached once per chapter when the last lesson is reached.
  const { trackFeedLessonSwiped, trackFeedChapterLastLessonReached } = useTrackFeedEvents();
  const prevLessonIdxRef = useRef<number | null>(null);
  const swipeTrackedChapterRef = useRef<number | null>(null);
  const lastLessonTrackedChapterRef = useRef<number | null>(null);

  useEffect(() => {
    const lessonId = chapter.lessonIDs[lessonIndex];
    if (lessonId === undefined) return;

    // A chapter change (vertical scroll) resets currentLessonIndex to 0 — that's
    // not a horizontal lesson swipe, so don't compare across chapters.
    if (swipeTrackedChapterRef.current !== chapter.chapterID) {
      swipeTrackedChapterRef.current = chapter.chapterID;
      prevLessonIdxRef.current = lessonIndex;
    }

    const prev = prevLessonIdxRef.current;
    if (prev !== null && prev !== lessonIndex) {
      trackFeedLessonSwiped(
        chapter.chapterID,
        lessonId,
        lessonIndex,
        lessonIndex > prev ? "right" : "left",
      );
    }
    prevLessonIdxRef.current = lessonIndex;

    const isLastLesson = lessonIndex === totalLessons - 1;
    if (isLastLesson && lastLessonTrackedChapterRef.current !== chapter.chapterID) {
      lastLessonTrackedChapterRef.current = chapter.chapterID;
      trackFeedChapterLastLessonReached(chapter.chapterID, lessonId, totalLessons);
    }
  }, [
    chapter.chapterID,
    chapter.lessonIDs,
    lessonIndex,
    totalLessons,
    trackFeedLessonSwiped,
    trackFeedChapterLastLessonReached,
  ]);

  const hasPrevious = lessonIndex > 0;
  const hasNext = lessonIndex < totalLessons - 1;

  // Only insert the quiz panel once an MCQ has been generated for this lesson
  // (happens after ~half the video) — so there are no dead-end "No activity"
  // panels to land on or get stuck in.
  const currentLessonId = chapter.lessonIDs[lessonIndex];
  const currentHasQuiz = (generatedActivities$[currentLessonId]?.get()?.length ?? 0) > 0;
  const showActivity = activityVisible$.get() && currentHasQuiz;

  // Panels: [prev-video?, current-video, current-activity?, next-video?]
  const panels: Array<
    | { type: "video"; lessonIndex: number; slot: "previous" | "current" | "next" }
    | { type: "activity"; lessonIndex: number }
  > = [];

  if (hasPrevious) panels.push({ type: "video", lessonIndex: lessonIndex - 1, slot: "previous" });
  panels.push({ type: "video", lessonIndex, slot: "current" });
  if (currentHasQuiz) panels.push({ type: "activity", lessonIndex });
  if (hasNext) panels.push({ type: "video", lessonIndex: lessonIndex + 1, slot: "next" });

  const currentVideoPanel = panels.findIndex(
    (p) => p.type === "video" && p.slot === "current",
  );
  // The activity panel (when present) sits immediately after the current video.
  const activePanelIndex = currentVideoPanel + (showActivity ? 1 : 0);

  // The arrow buttons live in a sibling subtree (LessonNavigation, inside
  // VideoLayout) and can't reach this scroll container directly. They emit a
  // directional request; we keep the live active-panel index and panel count in
  // a ref so the once-subscribed handler below always sees current values.
  const navRef = useRef({ activePanelIndex, panelCount: panels.length });
  navRef.current = { activePanelIndex, panelCount: panels.length };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    isResettingScroll.current = true;
    scrollPausedByUs.current = false;
    const expectedLeft = activePanelIndex * el.clientWidth;
    el.scrollTo({ left: expectedLeft, behavior: "instant" as ScrollBehavior });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isResettingScroll.current = false;
        const w = el.clientWidth;
        if (w && Math.abs(el.scrollLeft - activePanelIndex * w) > 1) {
          el.scrollTo({ left: activePanelIndex * w, behavior: "smooth" });
        }
      });
    });
  }, [lessonIndex, activePanelIndex]);

  const handleScrollEnd = useCallback(() => {
    if (isResettingScroll.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const panelWidth = el.clientWidth;
    if (panelWidth === 0) return;
    const snappedIndex = Math.round(el.scrollLeft / panelWidth);

    // One swipe = one step through the unified sequence. Same path as the
    // arrows and the activity card's button, so they can't disagree.
    if (snappedIndex > activePanelIndex) {
      stepForward();
    } else if (snappedIndex < activePanelIndex) {
      stepBackward();
    }
  }, [activePanelIndex]);

  const handleScroll = useCallback(() => {
    if (isResettingScroll.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const panelWidth = el.clientWidth;
    if (panelWidth === 0) return;
    const currentLeft = activePanelIndex * panelWidth;
    const scrollFraction = Math.abs(el.scrollLeft - currentLeft) / panelWidth;
    if (scrollFraction >= 0.5 && !scrollPausedByUs.current) {
      if (!isPaused$.peek()) {
        scrollPausedByUs.current = true;
        isPaused$.set(true);
      }
    } else if (scrollFraction < 0.5 && scrollPausedByUs.current) {
      scrollPausedByUs.current = false;
      isPaused$.set(false);
    }
  }, [activePanelIndex]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // iOS Safari before ~26 doesn't fire `scrollend`. Without it the lesson
    // carousel snaps but handleScrollEnd never runs, so stepForward/stepBackward
    // never fires and the PWA gets stuck on a panel. Feature-detect; when absent,
    // settle via a debounced scroll+touchend timer that also advances state.
    const hasScrollEnd = "onscrollend" in (el as object);
    if (hasScrollEnd) el.addEventListener("scrollend", handleScrollEnd);
    el.addEventListener("scroll", handleScroll, { passive: true });

    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const onScrollSettle = () => {
      if (hasScrollEnd || isResettingScroll.current) return;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const w = el.clientWidth;
        if (w) {
          const target = Math.round(el.scrollLeft / w) * w;
          if (Math.abs(el.scrollLeft - target) > 1) {
            el.scrollTo({ left: target, behavior: "smooth" });
          }
        }
        handleScrollEnd();
      }, 120);
    };
    if (!hasScrollEnd) el.addEventListener("scroll", onScrollSettle, { passive: true });

    let snapTimer: ReturnType<typeof setTimeout> | undefined;
    const onTouchEnd = () => {
      clearTimeout(snapTimer);
      let hadMomentum = false;
      const onScroll = () => { hadMomentum = true; };
      el.addEventListener("scroll", onScroll, { passive: true });
      snapTimer = setTimeout(() => {
        el.removeEventListener("scroll", onScroll);
        if (hadMomentum) return;
        const w = el.clientWidth;
        if (!w) return;
        const target = Math.round(el.scrollLeft / w) * w;
        if (Math.abs(el.scrollLeft - target) > 1) {
          el.scrollTo({ left: target, behavior: "smooth" });
        }
        // No momentum scroll fired, so the settle handler won't run — advance
        // state here too (only needed on the no-scrollend path).
        if (!hasScrollEnd) handleScrollEnd();
      }, 64);
    };
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      clearTimeout(snapTimer);
      clearTimeout(settleTimer);
      el.removeEventListener("scrollend", handleScrollEnd);
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("scroll", onScrollSettle);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [handleScrollEnd, handleScroll]);

  // Arrow / action-button taps: drive the SAME programmatic smooth scroll the
  // swipe settles through, instead of the instant index jump. We scroll to the
  // adjacent panel; the scrollend / settle path above then fires
  // stepForward/stepBackward, so the on-screen transition is identical to a
  // swipe and the shared step state stays authoritative (no desync). Subscribed
  // once; reads live geometry from navRef to avoid stale closures.
  useEffect(() => {
    return feedScrollRequest$.onChange(({ value }) => {
      const el = scrollRef.current;
      if (!el || isResettingScroll.current) return;
      const w = el.clientWidth;
      if (!w) return;
      const { activePanelIndex: active, panelCount } = navRef.current;
      const targetIndex =
        value.direction === "forward" ? active + 1 : active - 1;
      // Guard: the arrows disable themselves when stepping isn't possible, but
      // never scroll past the rendered panels.
      if (targetIndex < 0 || targetIndex >= panelCount) return;
      el.scrollTo({ left: targetIndex * w, behavior: "smooth" });
    });
    // feedScrollRequest$ is a stable module-level observable; subscribe once.
  }, []);

  return (
    <div className="h-full w-full">
      <div className={`h-full w-full ${VIDEO_MAX_W} mx-auto overflow-hidden`}>
        <div
          ref={scrollRef}
          className="h-full w-full flex overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollSnapType: "x mandatory", overscrollBehaviorX: "contain" }}
        >
          {panels.map((panel, panelIdx) => {
            const isActive = panelIdx === activePanelIndex;
            const lessonId = chapter.lessonIDs[panel.lessonIndex];
            return (
              <div
                key={
                  panel.type === "activity"
                    ? `activity-${panel.lessonIndex}`
                    : panel.slot === "current"
                    ? "video-current"
                    : panel.lessonIndex
                }
                className="shrink-0 w-full h-full"
                style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}
              >
                {panel.type === "video" ? (
                  <VideoLayout
                    chapterId={chapter.chapterID}
                    lessonId={lessonId}
                    isActive={isActive}
                    onToggleLike={onToggleLike}
                  />
                ) : (
                  <FeedActivityCarousel
                    chapterId={chapter.chapterID}
                    lessonId={lessonId}
                    isActive={isActive}
                    onContinue={() => {
                      // Finished/skipped this lesson's activities → next video,
                      // or roll into the next reel when it's the last lesson
                      // (matches iOS exhausting a chapter's activities).
                      if (hasNext) nextLesson(totalLessons);
                      else nextChapter();
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export { HorizontalLessonCarousel };
